import React, { useState } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import './AlertForm.css';
import { AMTRAK_STATIONS } from '../consts/amtrakStations.js';
import { timeToMilitary } from '../functions/timeToMilitary.js';
import { validateAddAlertFormData } from '../functions/validateAddAlertFormData.js';
import { emptyFormData } from '../consts/emptyFormData.js';

const AlertForm = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [formData, setFormData] = useState(emptyFormData);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const amtrakStationOptions = AMTRAK_STATIONS.map((station) => (
    <option key={station.code} value={station.code}>
      {station.name} ({station.code})
    </option>
  ))

  //define how to handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  //define how to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {

      // Validate form data
      validateAddAlertFormData(formData, setMessage, setLoading);

      // Convert time format to HHMM
      const minTime = timeToMilitary(formData.minArrivalTime);
      const maxTime = timeToMilitary(formData.maxArrivalTime);

      // Send to backend
      const payload = {
        departStation: formData.departStation,
        arriveStation: formData.arriveStation,
        travelDate: formData.travelDate,
        maxPrice: parseInt(formData.maxPrice),
        minArrivalTime: minTime,
        maxArrivalTime: maxTime,
        phoneNumber: formData.phoneNumber,
      };

      const token = await getAccessTokenSilently();

      const response = await axios.post(
        '/api/alerts',
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setMessage({ type: 'success', text: `Alert created successfully! (ID: ${response.data.id})`});

      // Reset form
      setFormData(emptyFormData);
    } catch (error) {
      const errorText = error.response?.data?.error || error.message || 'Failed to create alert';
      setMessage({ type: 'error', text: errorText});
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="alert-form-container">
      <form className="alert-form" onSubmit={handleSubmit}>
        <h2>Add New Alert</h2>

        {message.text && (
          <div className={`message message-${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="departStation">Departing Station *</label>
          <select id="departStation" name="departStation" value={formData.departStation} onChange={handleInputChange} required >
            <option value="">Select a station</option>
            {amtrakStationOptions}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="arriveStation">Arrival Station *</label>
          <select id="arriveStation" name="arriveStation" value={formData.arriveStation} onChange={handleInputChange} required >
            <option value="">Select a station</option>
            {amtrakStationOptions}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="travelDate">Travel Date *</label>
          <input id="travelDate" type="date" name="travelDate" value={formData.travelDate} onChange={handleInputChange} required />
        </div>

        <div className="form-group">
          <label htmlFor="maxPrice">Max Price (USD) *</label>
          <input id="maxPrice" type="number" name="maxPrice" value={formData.maxPrice} onChange={handleInputChange} placeholder="e.g., 150" min="1" step="1" required />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="minArrivalTime">Earliest Arrival Time *</label>
            <input id="minArrivalTime" type="time" name="minArrivalTime" value={formData.minArrivalTime} onChange={handleInputChange} required />
          </div>

          <div className="form-group">
            <label htmlFor="maxArrivalTime">Latest Arrival Time *</label>
            <input id="maxArrivalTime" type="time" name="maxArrivalTime" value={formData.maxArrivalTime} onChange={handleInputChange} required/>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="phoneNumber">Phone Number *</label>
          <input id="phoneNumber" type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} placeholder="e.g., +1234567890" required />
        </div>

        <button type="submit" className="submit-button" disabled={loading} >
          {loading ? 'Creating Alert...' : 'Create Alert'}
        </button>
      </form>
    </div>
  );
};

export default AlertForm;
